/**
 * Sign-in & Security: the hub for how a user gets into their account.
 *
 * PASSWORD routes to the set/change form. CONNECTED ACCOUNTS lists every
 * provider Flavrly supports -- iterating `ALL_SOCIAL_PROVIDERS`, never the
 * enabled list, so a provider that is switched off for NEW links is still
 * visible and unlinkable for anyone who already linked it. `isSocialProviderEnabled`
 * gates only the Connect action.
 *
 * Not platform split. The one web-specific concern here is the OAuth callback
 * error, which Better Auth reports as an `?error=` query param on the redirect
 * target rather than as a rejection from `linkSocial`. Reading and clearing it
 * needs only `useLocalSearchParams` and `router.setParams`, both of which are
 * cross-platform: the param simply never arrives on native. There is no
 * platform-conditional code to isolate, so a `.web`/`.native` pair would
 * duplicate the entire screen to gate one no-op.
 */

import { ScreenHeader } from '@/components/layout/screen-header';
import { Button } from '@/components/ui/button';
import { confirm } from '@/components/ui/confirmation-dialog';
import { Text } from '@/components/ui/text';
import { toast } from '@/components/ui/toast';
import { toLinkSocialError } from '@/features/auth/link-social-error';
import { linkApple, linkGoogle } from '@/features/auth/link-social';
import {
  ALL_SOCIAL_PROVIDERS,
  isSocialProviderEnabled,
  SOCIAL_PROVIDER_META,
} from '@/features/auth/providers';
import type { SocialProvider } from '@/features/auth/sign-in-social.types';
import { FAILED_TO_UNLINK_LAST_ACCOUNT, useAuth, type LinkedAccount } from '@/hooks/use-auth';
import { createLogger } from '@/utils/logger';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  ConnectedAccountRow,
  type ConnectedAccountAction,
} from './components/connected-account-row';
import { SettingsSection } from './components/settings-section';

const logger = createLogger('SECURITY_SCREEN');

const PASSWORD_ROUTE = '/(root)/password';

const COPY = {
  screenTitle: 'Sign-in & Security',
  intro: 'Manage how you sign in to Flavrly.',
  passwordSection: 'Password',
  passwordPending: 'Password',
  changePassword: 'Change password',
  setPassword: 'Set password',
  accountsSection: 'Connected Accounts',
  accountsIntro:
    'Link a provider to get another way in. You always need at least one way to sign in.',
  connect: 'Connect',
  disconnect: 'Disconnect',
  lastMethodNote:
    'This is your only way to sign in. Set a password first, then you can disconnect it.',
  loadError: 'We could not load your sign-in methods. Please try again.',
  retry: 'Try again',
  linkFailedTitle: 'Could not connect that account',
  linkFailedDescription: 'Please try again.',
  unlinkFailedTitle: 'Could not disconnect that account',
  unlinkFailedDescription: 'Please try again.',
  lastMethodTitle: 'That is your only way to sign in',
} as const;

/** The platform-resolved linker for each provider. */
const LINK_PROVIDER: Record<SocialProvider, () => Promise<void>> = {
  google: linkGoogle,
  apple: linkApple,
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View className="mb-6">
      <Text
        className="text-sm mb-2 uppercase font-medium text-muted-foreground"
        accessibilityRole="header"
      >
        {title}
      </Text>
      <View className="rounded-xl p-4 bg-card">{children}</View>
    </View>
  );
}

export function SecurityScreen() {
  const { checkHasPassword, fetchLinkedAccounts, unlinkAccount } = useAuth();
  const params = useLocalSearchParams<{ error?: string }>();

  // null while `checkHasPassword` resolves -- the row stays disabled until then,
  // mirroring how the delete screen guards its own unresolved password state.
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [accounts, setAccounts] = useState<LinkedAccount[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState<SocialProvider | null>(null);

  const applyAccounts = useCallback((list: LinkedAccount[]) => {
    setAccounts(list);
    setLoadFailed(false);
  }, []);

  /**
   * `fetchLinkedAccounts` REJECTS on failure by design, so a failed lookup can
   * never be mistaken for "no providers linked". That makes an explicit error
   * state mandatory rather than optional.
   */
  const applyLoadFailure = useCallback((error: unknown) => {
    logger.error('Could not load linked accounts:', error);
    setAccounts(null);
    setLoadFailed(true);
  }, []);

  /** Re-read the server's list. Used by retry, and after a link or unlink. */
  const loadAccounts = useCallback(async (): Promise<LinkedAccount[] | null> => {
    try {
      const list = await fetchLinkedAccounts();
      applyAccounts(list);
      return list;
    } catch (error) {
      applyLoadFailure(error);
      return null;
    }
  }, [fetchLinkedAccounts, applyAccounts, applyLoadFailure]);

  useEffect(() => {
    let cancelled = false;
    fetchLinkedAccounts()
      .then((list) => {
        if (!cancelled) applyAccounts(list);
      })
      .catch((error) => {
        if (!cancelled) applyLoadFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchLinkedAccounts, applyAccounts, applyLoadFailure]);

  useEffect(() => {
    let cancelled = false;
    checkHasPassword()
      .then((result) => {
        if (!cancelled) setHasPassword(result);
      })
      .catch(() => {
        // checkHasPassword already fails safe; this is belt and braces.
        if (!cancelled) setHasPassword(true);
      });
    return () => {
      cancelled = true;
    };
  }, [checkHasPassword]);

  // Web only in practice: the OAuth callback reports "this provider identity
  // belongs to a different Flavrly user" as a query param on the redirect back
  // here, not as an error from the linkSocial call.
  useEffect(() => {
    const code = params.error;
    if (!code) return;
    const linkError = toLinkSocialError({ code });
    toast.error(COPY.linkFailedTitle, { description: linkError.message });
    // Clear it so a re-render does not replay the toast.
    router.setParams({ error: undefined });
  }, [params.error]);

  const handleLink = async (provider: SocialProvider) => {
    setPending(provider);
    try {
      await LINK_PROVIDER[provider]();
      // Native resolves once the link is attached; web resolves with a redirect
      // in flight. Confirm against the server rather than assuming success.
      const list = await loadAccounts();
      if (list?.some((account) => account.providerId === provider)) {
        toast.success(`${SOCIAL_PROVIDER_META[provider].name} connected`);
      }
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : COPY.linkFailedDescription;
      toast.error(COPY.linkFailedTitle, { description: message });
    } finally {
      setPending(null);
    }
  };

  const handleUnlink = async (provider: SocialProvider) => {
    const meta = SOCIAL_PROVIDER_META[provider];
    const confirmed = await confirm({
      title: `Disconnect ${meta.name}?`,
      description: `You will no longer be able to sign in to Flavrly with ${meta.name}.`,
      confirmLabel: COPY.disconnect,
      variant: 'destructive',
    });
    if (!confirmed) return;

    setPending(provider);
    try {
      const result = await unlinkAccount(provider);
      if (result.error) {
        // The client-side guard below is a UX nicety; the server is the real
        // boundary and may still refuse.
        const isLastMethod = result.error.code === FAILED_TO_UNLINK_LAST_ACCOUNT;
        toast.error(isLastMethod ? COPY.lastMethodTitle : COPY.unlinkFailedTitle, {
          description: result.error.message || COPY.unlinkFailedDescription,
        });
        return;
      }
      toast.success(`${meta.name} disconnected`);
      await loadAccounts();
    } finally {
      setPending(null);
    }
  };

  const passwordLabel =
    hasPassword === null
      ? COPY.passwordPending
      : hasPassword
        ? COPY.changePassword
        : COPY.setPassword;

  const linkedIds = new Set((accounts ?? []).map((account) => account.providerId));
  // Every row counts, credential included: it is a sign-in method too.
  const isOnlyMethod = (accounts?.length ?? 0) <= 1;

  const buildAction = (
    provider: SocialProvider,
    connected: boolean
  ): ConnectedAccountAction | null => {
    if (connected) {
      return {
        label: COPY.disconnect,
        accessibilityLabel: `${COPY.disconnect} ${SOCIAL_PROVIDER_META[provider].name}`,
        variant: 'destructive',
        disabled: isOnlyMethod || pending !== null,
        loading: pending === provider,
        onPress: () => handleUnlink(provider),
      };
    }
    if (!isSocialProviderEnabled(provider)) return null;
    return {
      label: COPY.connect,
      accessibilityLabel: `${COPY.connect} ${SOCIAL_PROVIDER_META[provider].name}`,
      variant: 'outline',
      disabled: pending !== null,
      loading: pending === provider,
      onPress: () => handleLink(provider),
    };
  };

  const renderAccounts = () => {
    if (loadFailed) {
      return (
        <View accessibilityLiveRegion="polite">
          <Text testID="connected-accounts-error" className="text-sm text-destructive mb-3">
            {COPY.loadError}
          </Text>
          <Button
            variant="outline"
            size="sm"
            onPress={() => loadAccounts()}
            accessibilityLabel={COPY.retry}
            testID="connected-accounts-retry"
          >
            {COPY.retry}
          </Button>
        </View>
      );
    }

    if (accounts === null) {
      return (
        <View className="py-4 items-center" accessibilityLiveRegion="polite">
          <ActivityIndicator accessibilityLabel="Loading your sign-in methods" />
        </View>
      );
    }

    return ALL_SOCIAL_PROVIDERS.map((provider, index) => {
      const connected = linkedIds.has(provider);
      return (
        <ConnectedAccountRow
          key={provider}
          provider={provider}
          connected={connected}
          action={buildAction(provider, connected)}
          note={connected && isOnlyMethod ? COPY.lastMethodNote : undefined}
          isLast={index === ALL_SOCIAL_PROVIDERS.length - 1}
        />
      );
    });
  };

  return (
    <SafeAreaView className="flex-1" edges={['top']}>
      <ScreenHeader title={COPY.screenTitle} showBackButton />
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="px-4 pb-8"
      >
        <Text className="text-base mt-2 mb-6 text-muted-foreground">{COPY.intro}</Text>

        <SettingsSection
          title={COPY.passwordSection}
          items={[
            {
              testID: 'security-password-row',
              icon: 'key-outline',
              title: passwordLabel,
              disabled: hasPassword === null,
              action: () => router.push(PASSWORD_ROUTE),
            },
          ]}
        />

        <Section title={COPY.accountsSection}>
          <Text className="text-sm text-muted-foreground mb-2">{COPY.accountsIntro}</Text>
          {renderAccounts()}
        </Section>
      </ScrollView>
    </SafeAreaView>
  );
}
